import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const api = axios.create({ baseURL: `${API_BASE_URL}/api` });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bt_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('bt_token');
      const publicPaths = ['/login', '/register', '/forgot-password'];
      const isPublicPrefixed = ['/view/', '/reset-password/'].some((p) => window.location.pathname.startsWith(p));
      if (!publicPaths.includes(window.location.pathname) && !isPublicPrefixed) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Unauthenticated endpoints backing the public /view/:invoiceId share link
export const publicApi = {
  getInvoice: (id) => api.get(`/public/invoices/${id}`),
  downloadPdf: (id) => api.get(`/public/invoices/${id}/pdf`, { responseType: 'blob' }),
};

export const dashboard = {
  get: (period) => api.get('/dashboard', { params: period ? { period } : {} }),
};

export const businesses = {
  get: () => api.get('/businesses'),
  update: (id, data) => api.put(`/businesses/${id}`, data),
  nextInvoiceNumber: (id) => api.get(`/businesses/${id}/next-invoice-number`),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/businesses/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const clients = {
  list: () => api.get('/clients'),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

export const invoices = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  updateStatus: (id, status) => api.patch(`/invoices/${id}/status`, { status }),
  cancel: (id, opts) => api.post(`/invoices/${id}/cancel`, opts),
  delete: (id) => api.delete(`/invoices/${id}`),
  addPayment: (id, data) => api.post(`/invoices/${id}/payments`, data),
  deletePayment: (id, paymentId) => api.delete(`/invoices/${id}/payments/${paymentId}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  sendReminderEmail: (id) => api.post(`/invoices/${id}/send-reminder-email`),
  getReminderLogs: (id) => api.get(`/invoices/${id}/reminder-logs`),
};

export const reminders = {
  get: () => api.get('/reminders'),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  due: () => api.get('/reminders/due'),
};

export const subscription = {
  status: () => api.get('/subscription/status'),
  create: (billing_cycle) => api.post('/subscription/create', { billing_cycle }),
  verify: (data) => api.post('/subscription/verify', data),
  cancel: () => api.post('/subscription/cancel'),
};

// Separate axios instance + token for the admin panel — deliberately does not
// share the regular `api` instance or `bt_token`, so the two auth systems
// can't be mixed up client-side either.
export const adminApi = axios.create({ baseURL: `${API_BASE_URL}/api/admin` });

adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('bt_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('bt_admin_token');
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

export const adminAuth = {
  login: (data) => adminApi.post('/login', data),
  me: () => adminApi.get('/me'),
};

export const admin = {
  overview: () => adminApi.get('/overview'),
  businesses: (q) => adminApi.get('/businesses', { params: q ? { q } : {} }),
  business: (id) => adminApi.get(`/businesses/${id}`),
  updateBusinessStatus: (id, status) => adminApi.patch(`/businesses/${id}/status`, { status }),
};

export default api;
